import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dateTime, money } from '@ui/lib/format';
import { Badge, EmptyState, ErrorState, Modal, PageHead, Pager, Stat, TableSkeleton } from '@ui/components/ui';
import { api, qs } from '../lib/api';

interface TransferRow {
  id: string;
  method: string;
  amount: number;
  currency: string;
  reference: string | null;
  receiptUrl: string;
  status: string;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  tenant: { id: string; slug: string; name: string };
}

interface TransferPage {
  items: TransferRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  pending: number;
}

const METHOD_LABELS: Record<string, string> = {
  instapay: 'إنستاباي',
  vodafone_cash: 'فودافون كاش',
};

const STATUS_META: Record<string, { label: string; kind: 'warning' | 'success' | 'danger' }> = {
  pending: { label: 'قيد المراجعة', kind: 'warning' },
  confirmed: { label: 'مؤكَّدة', kind: 'success' },
  rejected: { label: 'مرفوضة', kind: 'danger' },
};

export function ManualPayments() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'' | 'pending' | 'confirmed' | 'rejected'>('pending');
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState<TransferRow | null>(null);
  const [reject, setReject] = useState<TransferRow | null>(null);

  const query = { status: status || undefined, page };
  const list = useQuery({
    queryKey: ['manual-payments', query],
    queryFn: () => api<TransferPage>(`/manual-payments${qs(query)}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['manual-payments'] });
    void queryClient.invalidateQueries({ queryKey: ['summary'] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };

  const confirm = useMutation({
    mutationFn: (id: string) => api(`/manual-payments/${id}/confirm`, { method: 'POST', body: {} }),
    onSuccess: refresh,
  });

  return (
    <div className="page">
      <PageHead
        title="الحوالات المحلّيّة"
        subtitle="تحويلات إنستاباي وفودافون كاش — راجع الإيصال ثمّ أكّد أو ارفض"
      />

      <div className="stats">
        <Stat label="قيد المراجعة" value={list.data?.pending ?? '—'} note="بانتظار حساباتنا" />
        <Stat label="في هذه الصفحة" value={list.data?.items.length ?? '—'} />
        <Stat label="الإجمالي" value={list.data?.total ?? '—'} note="ضمن التصفية" />
      </div>

      <div className="toolbar">
        <div className="row" style={{ gap: 2 }}>
          {[
            { key: 'pending', label: 'قيد المراجعة' },
            { key: 'confirmed', label: 'مؤكَّدة' },
            { key: 'rejected', label: 'مرفوضة' },
            { key: '', label: 'الكل' },
          ].map((s) => (
            <button
              key={s.key}
              className={`btn btn-sm${status === s.key ? ' btn-primary' : ''}`}
              onClick={() => {
                setStatus(s.key as '' | 'pending' | 'confirmed' | 'rejected');
                setPage(1);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>المستأجر</th>
                <th>الوسيلة</th>
                <th>المبلغ</th>
                <th>المرجع</th>
                <th>التاريخ</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={7} />}

            {list.data && (
              <tbody>
                {list.data.items.map((t) => {
                  const st = STATUS_META[t.status] ?? { label: t.status, kind: 'info' as const };
                  return (
                    <tr key={t.id}>
                      <td>
                        <Link to={`/tenants/${t.tenant.id}`} className="link">
                          {t.tenant.name}
                        </Link>
                        <div className="tiny mute num">{t.tenant.slug}</div>
                      </td>
                      <td>{METHOD_LABELS[t.method] ?? t.method}</td>
                      <td className="num cell-strong">
                        {money(t.amount, t.currency === 'EGP' ? 'ج.م' : t.currency)}
                      </td>
                      <td className="num cell-mute">{t.reference || '—'}</td>
                      <td className="num cell-mute">{dateTime(t.createdAt)}</td>
                      <td>
                        <Badge kind={st.kind}>{st.label}</Badge>
                        {t.status === 'rejected' && t.reviewNote && (
                          <div className="tiny mute">{t.reviewNote}</div>
                        )}
                      </td>
                      <td className="end">
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-sm" onClick={() => setPreview(t)}>
                            الإيصال
                          </button>
                          {t.status === 'pending' && (
                            <>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => confirm.mutate(t.id)}
                                disabled={confirm.isPending}
                              >
                                تأكيد
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => setReject(t)}
                                disabled={confirm.isPending}
                              >
                                رفض
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState title="لا حوالات" text="لم تُرفع حوالاتٌ محلّيّةٌ ضمن هذه التصفية." />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={setPage}
          />
        )}
      </div>

      {preview && (
        <Modal title={`إيصال ${METHOD_LABELS[preview.method] ?? preview.method}`} onClose={() => setPreview(null)} wide>
          <div className="mp-receipt-meta">
            <span>
              <strong>{preview.tenant.name}</strong> · {money(preview.amount, preview.currency === 'EGP' ? 'ج.م' : preview.currency)}
            </span>
            {preview.reference && (
              <span className="tiny mute num">مرجع: {preview.reference}</span>
            )}
          </div>
          <img className="mp-receipt-img" src={preview.receiptUrl} alt="إيصال التحويل" />
        </Modal>
      )}

      {reject && (
        <RejectDialog
          transfer={reject}
          onClose={() => setReject(null)}
          onDone={() => {
            setReject(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function RejectDialog({
  transfer,
  onClose,
  onDone,
}: {
  transfer: TransferRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      api(`/manual-payments/${transfer.id}/reject`, { method: 'POST', body: { reason: reason.trim() } }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="رفض الحوالة"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={mut.isPending}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            onClick={() => mut.mutate()}
            disabled={reason.trim().length < 2 || mut.isPending}
          >
            {mut.isPending ? 'جارٍ الرفض' : 'تأكيد الرفض'}
          </button>
        </>
      }
    >
      <p className="tiny mute" style={{ marginTop: 0 }}>
        سيظهر للمستأجرِ أنّ التحويلَ لم يُقبل مع السبب. لن تصدرَ فاتورةٌ ولن يُمَدَّ الاشتراك.
      </p>
      <label className="field">
        <span className="label">سبب الرفض</span>
        <textarea
          className="textarea"
          value={reason}
          maxLength={400}
          rows={3}
          placeholder="مثال: لم يصل التحويلُ إلى حسابنا / المبلغُ غير مطابق."
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {mut.isError && (
        <p className="tiny" style={{ color: 'var(--danger)' }}>
          {mut.error instanceof Error ? mut.error.message : 'تعذّر الرفض.'}
        </p>
      )}
    </Modal>
  );
}
